import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Circle, Calendar as CalendarIcon, ChevronLeft, ChevronRight, RefreshCw, Copy, Crown, CheckCircle, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { format } from "date-fns";
import { PRIORITY_CONFIG, API } from "@/utils/constants";

const CalendarSyncCard = ({ isPro, onShowUpgrade }) => {
  const [feed, setFeed] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isPro) return;
    axios.get(`${API}/calendar/sync`).then(r => {
      const feedUrl = `${API.replace(/\/api$/, '')}${r.data.feed_path}`;
      setFeed({ ...r.data, feed_url: feedUrl });
    }).catch(() => {});
  }, [isPro]);

  const copyUrl = () => {
    navigator.clipboard.writeText(feed?.feed_url || '');
    setCopied(true);
    toast.success("Feed URL copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isPro) {
    return (
      <div className="bg-card rounded-xl border p-4 mt-6" data-testid="calendar-sync-upsell">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><RefreshCw className="h-4 w-4 text-primary" />Calendar Sync<Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs"><Crown className="h-3 w-3 mr-1" />Pro</Badge></h3>
        <p className="text-sm text-muted-foreground mb-3">Sync your tasks to Google Calendar, Outlook, or Apple Calendar automatically.</p>
        <Button size="sm" onClick={onShowUpgrade} data-testid="calendar-sync-upgrade-btn"><Crown className="h-4 w-4 mr-2" />Upgrade to Pro</Button>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border p-4 mt-6" data-testid="calendar-sync-card">
      <h3 className="font-semibold mb-1 flex items-center gap-2"><RefreshCw className="h-4 w-4 text-primary" />Calendar Sync</h3>
      <p className="text-sm text-muted-foreground mb-3">Subscribe to this feed and your tasks (with due dates) appear in your personal or company calendar — updates automatically.</p>
      <div className="flex gap-2 mb-3">
        <Input readOnly value={feed?.feed_url || 'Loading…'} className="font-mono text-xs" data-testid="calendar-feed-url" />
        <Button variant="outline" size="icon" onClick={copyUrl} disabled={!feed} data-testid="copy-feed-url-btn">
          {copied ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      <div className="grid sm:grid-cols-3 gap-2 text-xs text-muted-foreground">
        <div className="bg-muted/40 rounded-lg p-2.5"><span className="font-medium text-foreground block mb-0.5">Google Calendar</span>Settings → Add calendar → From URL → paste</div>
        <div className="bg-muted/40 rounded-lg p-2.5"><span className="font-medium text-foreground block mb-0.5">Outlook</span>Add calendar → Subscribe from web → paste</div>
        <div className="bg-muted/40 rounded-lg p-2.5"><span className="font-medium text-foreground block mb-0.5">Apple Calendar</span>File → New Calendar Subscription → paste</div>
      </div>
    </div>
  );
};

export const CalendarView = ({ tasks, columns, onEditTask, isPro, onShowUpgrade, onAddTaskForDate }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarTasks, setCalendarTasks] = useState([]);

  useEffect(() => {
    const tasksWithDates = tasks.filter(t => t.due_date && !t.archived);
    setCalendarTasks(tasksWithDates);
  }, [tasks]);

  const getTasksForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarTasks.filter(t => t.due_date === dateStr);
  };

  const selectedDateTasks = getTasksForDate(selectedDate);

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const dayTasks = getTasksForDate(date);
    if (dayTasks.length === 0) return null;
    return (
      <div className="flex justify-center mt-1">
        <div className="flex gap-0.5">
          {dayTasks.slice(0, 3).map((t, i) => (
            <div key={i} className={`w-1.5 h-1.5 rounded-full ${t.completed ? 'bg-emerald-500' : t.priority === 'high' ? 'bg-red-500' : t.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'}`} />
          ))}
          {dayTasks.length > 3 && <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />}
        </div>
      </div>
    );
  };

  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return '';
    const dayTasks = getTasksForDate(date);
    if (dayTasks.some(t => !t.completed && t.priority === 'high')) return 'calendar-tile-high';
    if (dayTasks.length > 0) return 'calendar-tile-has-tasks';
    return '';
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-card rounded-xl border p-4">
            <ReactCalendar
              onChange={setSelectedDate}
              value={selectedDate}
              tileContent={tileContent}
              tileClassName={tileClassName}
              className="opero-calendar"
              prevLabel={<ChevronLeft className="h-4 w-4" />}
              nextLabel={<ChevronRight className="h-4 w-4" />}
              prev2Label={null}
              next2Label={null}
            />
          </div>
          <CalendarSyncCard isPro={isPro} onShowUpgrade={onShowUpgrade} />
        </div>
        <div className="bg-card rounded-xl border p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-primary" />
            {format(selectedDate, 'MMMM d, yyyy')}
          </h3>
          <Button size="sm" variant="outline" className="w-full mb-3" data-testid="calendar-add-task-btn"
            onClick={() => isPro ? onAddTaskForDate(selectedDate) : onShowUpgrade()}>
            <Plus className="h-4 w-4 mr-1.5" />Add task on this date
            {!isPro && <Crown className="h-3.5 w-3.5 ml-1.5 text-amber-500" />}
          </Button>
          <ScrollArea className="h-[400px]">
            {selectedDateTasks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">No tasks due on this date</div>
            ) : (
              <div className="space-y-2">
                {selectedDateTasks.map(task => {
                  const column = columns.find(c => c.id === task.column_id);
                  return (
                    <button key={task.id} onClick={() => onEditTask(task)} className="w-full text-left p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid={`calendar-task-${task.id}`}>
                      <div className="flex items-center gap-2">
                        {task.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                        <span className={`font-medium text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="secondary" className={`text-xs ${PRIORITY_CONFIG[task.priority].className}`}>{PRIORITY_CONFIG[task.priority].label}</Badge>
                        {column && <Badge variant="outline" className="text-xs"><span className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: column.color }} />{column.name}</Badge>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};
